/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useGetBreeds } from '@application/api';
import { useValidation } from '@application/utils';
import type { MtLitterGetLitterQuery } from '@gql/graphql';
import type { MtLitterPageFormValues } from '@presentation/pages';
import dayjs from 'dayjs';
import type { FormikErrors } from 'formik';
import { set } from 'lodash/fp';
import { useCallback } from 'react';

export const useValidateForm = () => {
  const {
    requiredMessage,
    invalidMessage,
    diffAvailabilityBirth,
    minPriceMessage,
    imageIsRequired,
    litterInvalidPedigree,
  } = useValidation();

  const { breedOptions } = useGetBreeds();

  const validate = useCallback(
    (
      litter:
        | NonNullable<
            NonNullable<MtLitterGetLitterQuery['accountMe']>['dogBreeder']
          >['litters'][number]
        | undefined,
      values: MtLitterPageFormValues,
    ) => {
      let errors: FormikErrors<MtLitterPageFormValues> = {};

      if (!values.description) {
        errors.description = requiredMessage;
      }

      if (values.fixedPrice == null) {
        errors.fixedPrice = requiredMessage;
      }

      if (values.fixedPrice === true) {
        if (!values.price) {
          errors.price = requiredMessage;
        } else if (values.price <= 0) {
          errors.price = minPriceMessage;
        }
      }

      if (values.fixedPrice === false) {
        if (!values.priceFrom) {
          errors = set(['priceFrom'], requiredMessage, errors);
        }
        if (!values.priceTo) {
          errors = set(['priceTo'], requiredMessage, errors);
        }
        if (values.priceFrom && values.priceTo && values.priceFrom > values.priceTo) {
          errors = set(['priceFrom'], minPriceMessage, errors);
        }
      }

      if (values.pedigree === undefined) {
        errors.pedigree = requiredMessage;
      } else if (
        values.pedigree !== null &&
        (values.fatherPedigree === undefined || values.motherPedigree === undefined)
      ) {
        errors.pedigree = litterInvalidPedigree;
      }

      if (values.birthday == null) {
        errors.birthday = requiredMessage;
      }

      if (
        values.birthday &&
        values.availabilityDate &&
        values.availabilityDate.diff(values.birthday, 'weeks') < 7
      ) {
        errors.availabilityDate = diffAvailabilityBirth;
      }

      if (!values.breedId || !breedOptions.find((bo) => bo.value === values.breedId)) {
        errors.breedId = requiredMessage;
      }

      if (values.born) {
        if (values.availabilityDate == null) {
          errors.availabilityDate = requiredMessage;
        }

        if (values.dewormed == null) {
          errors.dewormed = requiredMessage;
        }

        if (values.birthday && +values.birthday > +dayjs()) {
          errors.birthday = invalidMessage;
        }

        const totalDogs = (values.femalesCount ?? 0) + (values.malesCount ?? 0);
        const wasBorn = litter?.born;
        if (!wasBorn && !totalDogs && values.born) {
          errors.femalesCount = requiredMessage;
          errors.malesCount = requiredMessage;
        }

        if (values.vaccinated == null) {
          errors.vaccinated = requiredMessage;
        } else if (values.vaccinated === true && values.vaccinationDate == null) {
          errors.vaccinationDate = requiredMessage;
        }

        if (values.microchipped == null) {
          errors.microchipped = requiredMessage;
        }

        if (
          values.femalesLeftCount === undefined ||
          values.femalesLeftCount == null ||
          values.femalesLeftCount < 0
        ) {
          errors.femalesLeftCount = requiredMessage;
        } else if (values?.femalesCount && values.femalesLeftCount > values.femalesCount) {
          errors.femalesLeftCount = minPriceMessage;
        }

        if (
          values.malesLeftCount === undefined ||
          values.malesLeftCount == null ||
          values.malesLeftCount < 0
        ) {
          errors.malesLeftCount = requiredMessage;
        } else if (values?.malesCount && values.malesLeftCount > values.malesCount) {
          errors.malesLeftCount = minPriceMessage;
        }
      }

      if (!values.MOTHER) {
        errors.MOTHER = requiredMessage;
      }

      if (!values.FATHER) {
        errors.FATHER = requiredMessage;
      }

      if (!Array.isArray(values.images) || !values.images?.filter((item) => item).length) {
        errors = set(['images', 0], imageIsRequired, errors);
      }

      return errors;
    },
    [
      breedOptions,
      diffAvailabilityBirth,
      imageIsRequired,
      invalidMessage,
      litterInvalidPedigree,
      minPriceMessage,
      requiredMessage,
    ],
  );

  return { validate };
};
